import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { stationService } from '../services/api';
import { wsService } from '../services/websocket';

export const useStations = (filters = {}) => {
  // Debug: Tracer les appels du hook
  console.log('🔍 useStations appelé avec filters:', filters);

  // Serialized filters for memoization
  const filtersString = JSON.stringify(filters);

  // Memo des filters pour éviter les re-renders inutiles
  const memoizedFilters = useMemo(() => filters, [filtersString]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const isLoadingRef = useRef(false); // Protection avec useRef (pas de dépendance)
  const lastCallTimeRef = useRef(0); // Protection temporelle avec useRef

  // Fonction pour charger les stations - AVEC PROTECTIONS RENFORCÉES
  const loadStations = useCallback(async () => {
    const now = Date.now();
    
    // Protection contre les appels multiples
    if (isLoadingRef.current) {
      console.log('⚠️ Requête déjà en cours, ignorée');
      return;
    }
    
    // Protection temporelle: minimum 2 secondes entre les appels
    if (now - lastCallTimeRef.current < 2000) {
      console.log('🛑 Appel trop rapide, ignoré (moins de 2s)');
      return;
    }
    
    try {
      isLoadingRef.current = true;
      lastCallTimeRef.current = now;
      setLoading(true);
      setError(null);
      
      console.log('🔄 Chargement des stations... (timestamp:', now, ')');
      const data = await stationService.getStations(memoizedFilters);
      setStations(data);
      setLastUpdate(new Date());
      console.log('✅ Stations chargées:', data.length);
    } catch (err) {
      console.error('❌ Erreur lors du chargement des stations:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des stations');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [memoizedFilters]);

  // Fonction pour rafraîchir les données
  const refresh = useCallback(() => {
    loadStations();
  }, [loadStations]);

  // Mettre à jour une station spécifique
  const updateStation = useCallback((updatedStation) => {
    setStations(prevStations => 
      prevStations.map(station => 
        station.station_id === updatedStation.station_id 
          ? { ...station, ...updatedStation }
          : station
      )
    );
  }, []);

  // Ajouter une nouvelle station
  const addStation = useCallback((newStation) => {
    setStations(prevStations => {
      const exists = prevStations.some(s => s.station_id === newStation.station_id);
      if (exists) {
        return prevStations.map(s => 
          s.station_id === newStation.station_id ? { ...s, ...newStation } : s
        );
      }
      return [...prevStations, newStation];
    });
  }, []);

  // Supprimer une station
  const removeStation = useCallback((stationId) => {
    setStations(prevStations => 
      prevStations.filter(station => station.station_id !== stationId)
    );
  }, []);

  // Effet pour charger les stations au montage - AVEC DEBUG
  useEffect(() => {
    console.log('🚀 useEffect loadStations déclenché');
    loadStations();
  }, [loadStations]);

  // Effet pour écouter les mises à jour WebSocket
  useEffect(() => {
    const handleStationUpdate = (data) => {
      console.log('📡 Mise à jour station reçue:', data);
      
      switch (data.type) {
        case 'heartbeat':
          updateStation({
            station_id: data.station_id,
            last_seen: data.timestamp,
            status: 'online'
          });
          break;
        
        case 'status_update':
          updateStation({
            station_id: data.station_id,
            ...data.data
          });
          break;
        
        case 'station_connected':
          addStation(data.station);
          break;
        
        case 'station_disconnected':
          updateStation({
            station_id: data.station_id,
            status: 'offline',
            last_seen: data.timestamp
          });
          break;
        
        default:
          console.log('Type de mise à jour station non géré:', data.type);
      }
    };

    // S'abonner aux mises à jour WebSocket
    wsService.on('station_update', handleStationUpdate);

    // Se désabonner au démontage
    return () => {
      wsService.off('station_update', handleStationUpdate);
    };
  }, [updateStation, addStation]);

  return {
    stations,
    loading,
    error,
    lastUpdate,
    refresh,
    updateStation,
    addStation,
    removeStation,
    // Méthodes utilitaires
    getStationById: (id) => stations.find(s => s.station_id === id),
    getOnlineStations: () => stations.filter(s => s.status === 'online'),
    getOfflineStations: () => stations.filter(s => s.status === 'offline'),
    getCriticalStations: () => stations.filter(s => s.status === 'critical'),
    getStationsCount: () => stations.length,
  };
};

export default useStations;

